import { Injectable } from '@angular/core';
import { Subject, Observable, timer, of } from 'rxjs';
import {
  IBaseEntityType,
  IBulkUpdateDocumentsResult,
  ITask,
  IDocumentOperationOptions,
  TASK_STATUS,
  ITaskOptions,
} from '@app/shared/types/entities/base/base.type';
import { rx, observableOf } from '@app/shared/rx/rx-wrapper';
import { generateProgressBarLoadingParams } from '@app/shared/utils/task.utils';
import {
  merge,
  map,
  gt,
  includes,
  filter,
  isNil,
  assign,
  keys,
  reduce,
  snakeCase,
} from 'lodash';
import { BaseService } from '../base';
import { IPdfData } from '@app/shared/common/make-pdf/v2/pdf-data.type';
import { MAX_TASK_UPDATE_TIME } from '@config/global-constants';
import { FormGroup } from '@angular/forms';
import { ITransferGroupFormFieldPath } from '@shared/types';
import { markTransferItemsAcknowledge } from '@shared/services/transfer-group/utils/transfer-items-utils';
import { Primitive } from '@geniuspos/ui-shared';
import { toDate } from '@shared/utils';

@Injectable()
export class TaskService extends BaseService {
  public getTaskById(
    taskId: string,
    options: IDocumentOperationOptions = {},
  ): Observable<ITask> {
    const headers = this.composeHeadersForUpdateRequest(options);
    const url = `/task/${taskId}`;

    return this.http
      .get<ITask>(url, { headers })
      .pipe(this.catchHttpError(options.errorOverride));
  }

  public getTaskStatus(
    taskId: string,
    options?: {
      currentStep?: number;
      stepCount?: number;
      loopInterval?: number;
    },
  ): Observable<IBulkUpdateDocumentsResult | any> {
    const { currentStep, stepCount, loopInterval } = merge(
      { loopInterval: 5000 },
      options,
    );
    const dmSyncFinish$ = new Subject();

    return timer(1000, loopInterval)
      .pipe(
        rx.switchMap(() => this.getTaskById(taskId)),
        rx.tap((task: ITask) => {
          this.uiActions.enableLoader(
            generateProgressBarLoadingParams({
              currentStep,
              stepCount,
              task,
              i18nService: this.i18nService,
            }),
          );
          this.uiActions.preventLoaderCloseWhenTimeout(true);
          const time = Date.now() - toDate(task.updated_at).getTime();
          if (
            includes(
              [TASK_STATUS.failed, TASK_STATUS.completed],
              task.status,
            ) ||
            gt(time, MAX_TASK_UPDATE_TIME)
          ) {
            dmSyncFinish$.next(task);
          }
        }),
        rx.skipUntil(dmSyncFinish$),
        rx.take(1),
      )
      .pipe(rx.delay(300)); // For better user experience, here is a 300ms delay to let the user see the full progress.
  }

  public getDmSyncResult(
    items: IBaseEntityType[],
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult | any> {
    const { isSync, noLoader, currentStep, stepCount } = merge(
      {
        isSync: false,
        noLoader: false,
      },
      options,
    );
    const apiConfig = { apiEndpoint: 'transfer', endpoint: 'dm-sync' };

    return this.getTaskResult(
      { transfer_ids: map(items, '_id'), is_sync: isSync },
      {
        isSync,
        noLoader,
        currentStep,
        stepCount,
        apiConfig,
      },
    );
  }

  public getBulkChangeStatusResult(
    body: {
      transferIds: string[];
      isSync: boolean;
      toStatus: string;
    },
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult | any> {
    const { isSync, noLoader, currentStep, stepCount } = merge(
      {
        isSync: false,
        noLoader: false,
      },
      options,
    );
    const apiConfig = {
      apiEndpoint: 'transfer',
      endpoint: 'bulk-change-status',
    };
    const requestBody = reduce(
      keys(body),
      (obj, k) => assign(obj, { [snakeCase(k)]: body[k] }),
      {},
    );

    return this.getTaskResult(requestBody, {
      isSync,
      noLoader,
      currentStep,
      stepCount,
      apiConfig,
    });
  }

  public getInventoryCountDmSyncResult(
    body: {
      inventoryCountIds: string[];
    },
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult | any> {
    const { isSync, noLoader, currentStep, stepCount } = merge(
      {
        isSync: false,
        noLoader: false,
      },
      options,
    );
    const apiConfig = { apiEndpoint: 'inventory-count', endpoint: 'dm-sync' };

    return this.getTaskResult(
      { inventory_count_ids: body.inventoryCountIds, is_sync: isSync },
      {
        isSync,
        noLoader,
        currentStep,
        stepCount,
        apiConfig,
      },
    );
  }

  public getInventoryCountBulkChangeStatusResult(
    body: {
      inventoryCountIds: string[];
      toStatus: string;
    },
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult | any> {
    const { isSync, noLoader, currentStep, stepCount } = merge(
      {
        isSync: false,
        noLoader: false,
      },
      options,
    );
    const apiConfig = {
      apiEndpoint: 'inventory-count',
      endpoint: 'bulk-change-status',
    };
    const requestBody = reduce(
      keys(body),
      (obj, k) => assign(obj, { [snakeCase(k)]: body[k] }),
      {},
    );

    return this.getTaskResult(
      { ...requestBody, is_sync: isSync },
      {
        isSync,
        noLoader,
        currentStep,
        stepCount,
        apiConfig,
      },
    );
  }

  public getTaskResult(
    body: any,
    options?: ITaskOptions & {
      apiConfig?: {
        apiEndpoint?: string;
        endpoint?: string;
      };
    },
  ): Observable<IBulkUpdateDocumentsResult | any> {
    const { isSync, noLoader, apiConfig, currentStep, stepCount } = merge(
      {
        isSync: false,
        noLoader: false,
      },
      options,
    );

    return of(true).pipe(
      rx.tap(() => {
        if (!noLoader) {
          this.uiActions.enableLoader(
            isSync
              ? null
              : generateProgressBarLoadingParams({
                  currentStep,
                  stepCount,
                  i18nService: this.i18nService,
                }),
          );
        }
      }),
      rx.switchMap(() =>
        this.createTask(body, apiConfig).pipe(
          rx.switchMap((task: ITask) =>
            isSync
              ? observableOf(task)
              : this.getTaskStatus(task._id, {
                  currentStep,
                  stepCount,
                }),
          ),
        ),
      ),
      rx.finalize(() => {
        if (!noLoader) {
          this.uiActions.disableLoader();
        }
      }),
    );
  }

  public getPdfBulkDataWithSyncLoaderFunc(
    isSync: boolean,
    form: FormGroup = null,
    formFieldPath: ITransferGroupFormFieldPath = null,
  ): (selectedItems: IBaseEntityType[]) => Promise<IPdfData[]> {
    return async (selectedItems: IBaseEntityType[]): Promise<IPdfData[]> => {
      if (!isSync) {
        this.uiActions.enableLoader(
          generateProgressBarLoadingParams({ i18nService: this.i18nService }),
        );
      }

      const dmSyncResult = await this.getDmSyncResult(selectedItems, {
        isSync,
        noLoader: true,
      }).toPromise();

      if (!isNil(form) && !isNil(formFieldPath)) {
        markTransferItemsAcknowledge(
          map(
            filter(map(dmSyncResult.sub_tasks, 'value'), 'is_acknowledge'),
            'transfer_id',
          ),
          form,
          formFieldPath,
        );
      }

      return selectedItems;
    };
  }

  public createTask(
    body: {
      is_sync: boolean;
      [field: string]: Primitive | object;
    },
    apiConfig?: {
      apiEndpoint?: string;
      endpoint?: string;
    },
    options: IDocumentOperationOptions = {},
  ): Observable<ITask> {
    const { apiEndpoint, endpoint } = merge(
      {
        apiEndpoint: 'transfer',
        endpoint: 'dm-sync',
      },
      apiConfig,
    );

    const headers = this.composeHeadersForUpdateRequest(options);
    const url = `/${apiEndpoint}/${endpoint}`;

    return this.http.patch<any>(url, body, { headers }).pipe(
      this.catchHttpError(options.errorOverride, false, false, {
        throwError: true,
      }),
    );
  }
}
